import {
  SCREEN_EDITOR_SECTION_GUIDE_ANCHORS,
  type GuideAnchorDefinition,
} from '@modules/guides/anchors/guideAnchorRegistry';
import type { V2SectionType } from '@modules/reactFlow/config/v2ScreenTypeConfig';

export type ScreenEditorSectionGuideCategory =
  | 'shared'
  | 'unique'
  | 'paywall'
  | 'flexible';

export interface ScreenEditorSectionGuideDefinition {
  sectionType: V2SectionType;
  title: string;
  category: ScreenEditorSectionGuideCategory;
  sectionTestId: string;
  guideAnchor: GuideAnchorDefinition;
}

export const SCREEN_EDITOR_SECTION_GUIDE_DEFINITIONS = [
  {
    sectionType: 'headerBar',
    title: 'Header',
    category: 'shared',
    sectionTestId: 'section-header',
    guideAnchor: SCREEN_EDITOR_SECTION_GUIDE_ANCHORS.headerBar,
  },
  {
    sectionType: 'actionBar',
    title: 'Action Bar',
    category: 'shared',
    sectionTestId: 'section-action-bar',
    guideAnchor: SCREEN_EDITOR_SECTION_GUIDE_ANCHORS.actionBar,
  },
  {
    sectionType: 'backdrop',
    title: 'Background',
    category: 'shared',
    sectionTestId: 'section-background',
    guideAnchor: SCREEN_EDITOR_SECTION_GUIDE_ANCHORS.backdrop,
  },
  {
    sectionType: 'copyBlock',
    title: 'Content',
    category: 'shared',
    sectionTestId: 'section-content',
    guideAnchor: SCREEN_EDITOR_SECTION_GUIDE_ANCHORS.copyBlock,
  },
  {
    sectionType: 'basicConfig',
    title: 'Basic Config',
    category: 'shared',
    sectionTestId: 'section-basic-config',
    guideAnchor: SCREEN_EDITOR_SECTION_GUIDE_ANCHORS.basicConfig,
  },
  {
    sectionType: 'media',
    title: 'Media',
    category: 'shared',
    sectionTestId: 'section-media',
    guideAnchor: SCREEN_EDITOR_SECTION_GUIDE_ANCHORS.media,
  },
  {
    sectionType: 'options',
    title: 'Options',
    category: 'shared',
    sectionTestId: 'section-options',
    guideAnchor: SCREEN_EDITOR_SECTION_GUIDE_ANCHORS.options,
  },
  {
    sectionType: 'variableBinding',
    title: 'Variable Binding',
    category: 'shared',
    sectionTestId: 'section-variable-binding',
    guideAnchor: SCREEN_EDITOR_SECTION_GUIDE_ANCHORS.variableBinding,
  },
  {
    sectionType: 'animation',
    title: 'Animation',
    category: 'shared',
    sectionTestId: 'section-animation',
    guideAnchor: SCREEN_EDITOR_SECTION_GUIDE_ANCHORS.animation,
  },
  {
    sectionType: 'textField',
    title: 'Text Field',
    category: 'unique',
    sectionTestId: 'section-text-field',
    guideAnchor: SCREEN_EDITOR_SECTION_GUIDE_ANCHORS.textField,
  },
  {
    sectionType: 'carousel',
    title: 'Carousel',
    category: 'unique',
    sectionTestId: 'section-carousel',
    guideAnchor: SCREEN_EDITOR_SECTION_GUIDE_ANCHORS.carousel,
  },
  {
    sectionType: 'stepper',
    title: 'Stepper',
    category: 'unique',
    sectionTestId: 'section-stepper',
    guideAnchor: SCREEN_EDITOR_SECTION_GUIDE_ANCHORS.stepper,
  },
  {
    sectionType: 'roller',
    title: 'Roller',
    category: 'unique',
    sectionTestId: 'section-roller',
    guideAnchor: SCREEN_EDITOR_SECTION_GUIDE_ANCHORS.roller,
  },
  {
    sectionType: 'embed',
    title: 'Custom HTML',
    category: 'unique',
    sectionTestId: 'section-embed',
    guideAnchor: SCREEN_EDITOR_SECTION_GUIDE_ANCHORS.embed,
  },
  {
    sectionType: 'paywallHeader',
    title: 'Paywall Header',
    category: 'paywall',
    sectionTestId: 'section-paywall-header',
    guideAnchor: SCREEN_EDITOR_SECTION_GUIDE_ANCHORS.paywallHeader,
  },
  {
    sectionType: 'paywallMedia',
    title: 'Paywall Media',
    category: 'paywall',
    sectionTestId: 'section-paywall-media',
    guideAnchor: SCREEN_EDITOR_SECTION_GUIDE_ANCHORS.paywallMedia,
  },
  {
    sectionType: 'paywallBody',
    title: 'Paywall Body',
    category: 'paywall',
    sectionTestId: 'section-paywall-body',
    guideAnchor: SCREEN_EDITOR_SECTION_GUIDE_ANCHORS.paywallBody,
  },
  {
    sectionType: 'paywallSubscriptions',
    title: 'Paywall Subscriptions',
    category: 'paywall',
    sectionTestId: 'section-paywall-subscriptions',
    guideAnchor: SCREEN_EDITOR_SECTION_GUIDE_ANCHORS.paywallSubscriptions,
  },
  {
    sectionType: 'paywallFooter',
    title: 'Paywall Footer',
    category: 'paywall',
    sectionTestId: 'section-paywall-footer',
    guideAnchor: SCREEN_EDITOR_SECTION_GUIDE_ANCHORS.paywallFooter,
  },
  {
    sectionType: 'flexibleSections',
    title: 'Flexible Sections',
    category: 'flexible',
    sectionTestId: 'section-flexible-sections',
    guideAnchor: SCREEN_EDITOR_SECTION_GUIDE_ANCHORS.flexibleSections,
  },
  {
    sectionType: 'stickyContainer',
    title: 'Sticky Container',
    category: 'flexible',
    sectionTestId: 'section-sticky-container',
    guideAnchor: SCREEN_EDITOR_SECTION_GUIDE_ANCHORS.stickyContainer,
  },
] as const satisfies readonly ScreenEditorSectionGuideDefinition[];

export const SCREEN_EDITOR_SECTION_GUIDE_DEFINITIONS_BY_SECTION = Object.fromEntries(
  SCREEN_EDITOR_SECTION_GUIDE_DEFINITIONS.map(definition => [definition.sectionType, definition]),
) as Partial<Record<V2SectionType, ScreenEditorSectionGuideDefinition>>;

export const SCREEN_EDITOR_SECTION_GUIDE_ANCHORS_BY_TEST_ID = Object.fromEntries(
  SCREEN_EDITOR_SECTION_GUIDE_DEFINITIONS.map(definition => [
    definition.sectionTestId,
    definition.guideAnchor,
  ]),
) as Record<string, GuideAnchorDefinition>;
